import {
  GetWasteShipmentQuery,
  ShippingDocumentKind,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import pluralize from "pluralize";

import { attachedDocumentTitle } from "./reconciliationDisplay";
import { formatShipmentDate } from "./shipmentDisplay";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/**
 * `terminal` is for a shipment that stopped rather than finished. It reads as
 * halted instead of merely unfinished, which `upcoming` and `inactive` both
 * imply -- those say "not yet", and a canceled shipment's remaining steps are
 * never going to happen.
 */
export type LifecycleState = "done" | "current" | "upcoming" | "inactive" | "terminal";

/**
 * Stable identity for a step, independent of its label.
 *
 * The labels move as the shipment ages -- "Request pickup" becomes "Requested",
 * "Shipped" becomes "Canceled" -- so anything keyed to a step (a React key, the
 * action rendered under it) has to key off this instead.
 */
export type LifecycleStepKey = "packaged" | "request" | "shipped" | "manifest";

export type LifecycleStep = {
  key: LifecycleStepKey;
  label: string;
  state: LifecycleState;
  meta: string;
};

/** The export format a signed request URL was rendered in, for the menu label. */
export function formatFromExportUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  const match = /\.(pdf|csv|xlsx)(\?|$)/i.exec(url);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Just the fields the rail reads, so the reading of "what has happened" can be
 * exercised without standing up a whole shipment to do it — the same reason
 * `missingManifestTotalReason` takes its two fields rather than a parent.
 *
 * `lines` and `manifest` are the loose ones on purpose: only the container count
 * and whether the manifest row is present are read here, so narrowing them
 * further would tie this to shapes it does not care about.
 */
export type LifecycleShipment = Pick<
  Shipment,
  | "status"
  | "createdAt"
  | "requestedAt"
  | "requestGeneratedAt"
  | "requestExportUrl"
  | "plannedShipDate"
  | "scheduledPickupDate"
  | "shippedDate"
  | "manifestReceivedAt"
  | "manifestSigned"
> & {
  lines: readonly unknown[];
  manifest?: { id: string } | null;
  /**
   * Which kind of paper is attached, or null when none is. This decides the
   * paperwork step, not `manifestReceivedAt`: that column records WHEN a
   * document arrived, not whether one is attached, and it is stamped by the
   * bill-of-lading attach too — so reading it left a shipment carrying only a
   * bill of lading reporting a manifest it does not have.
   */
  reconciliation: { attachedDocumentKind?: ShippingDocumentKind | null };
};

/**
 * Where the shipment has got to, as ordered steps.
 *
 * Shared by the rail. Kept as data rather than markup so the same reading of
 * "what has happened" can be rendered vertically here and, if it is ever
 * needed, horizontally elsewhere.
 */
export function lifecycleSteps(shipment: LifecycleShipment): LifecycleStep[] {
  const isDraft = shipment.status === WasteShipmentStatus.Draft;
  const isRequested = shipment.status === WasteShipmentStatus.Requested;
  const isShipped = shipment.status === WasteShipmentStatus.Shipped;
  const isCanceled = shipment.status === WasteShipmentStatus.Canceled;
  const hasDocument = Boolean(shipment.requestGeneratedAt);
  /**
   * Whether the vendor was ever asked, which has to outlive the status.
   *
   * Two sources because neither alone is enough. `REQUESTED` is proof in itself
   * — the shipment is sitting on the step — and is the only evidence for rows
   * that predate `requestedAt`, which is not backfilled. But it stops being true
   * the moment the shipment ships, which is what made this step go green and then
   * revert to "never happened" for a request arranged outside Encamp; past that
   * point only the stamp knows.
   *
   * Deliberately NOT `status !== Draft`: a shipment can be marked shipped
   * straight from draft, so that would claim a request on shipments that skipped
   * the step entirely.
   */
  const wasRequested = shipment.requestedAt != null || isRequested;
  const containerCount = shipment.lines.length;

  const packaged: LifecycleStep = {
    key: "packaged",
    label: "Packaged",
    state: "done",
    meta: `${formatShipmentDate(shipment.createdAt)} · ${containerCount} ${pluralize(
      "container",
      containerCount
    )}`,
  };

  const requested: LifecycleStep = (() => {
    if (hasDocument) {
      const format = formatFromExportUrl(shipment.requestExportUrl);
      return {
        key: "request",
        label: "Requested",
        state: "done",
        meta: [formatShipmentDate(shipment.requestGeneratedAt), format].filter(Boolean).join(" · "),
      };
    }
    if (wasRequested) {
      // The bare transition: someone arranged the pickup outside Encamp, so
      // there is no document and no generated-at stamp to show. Keyed to
      // requestedAt rather than the status, so it still reads as reached once
      // the shipment ships — the request happened either way.
      return { key: "request", label: "Requested", state: "done", meta: "Arranged outside Encamp" };
    }
    if (isDraft) {
      return { key: "request", label: "Request pickup", state: "current", meta: "Optional" };
    }
    // Shipped or canceled having never been requested. "Not requested" rather
    // than "No document", which was written when this branch also caught
    // requests made without one and so had to describe the weaker fact.
    return { key: "request", label: "Request pickup", state: "inactive", meta: "Not requested" };
  })();

  const shipped: LifecycleStep = (() => {
    if (isShipped) {
      return {
        key: "shipped",
        label: "Shipped",
        state: "done",
        meta: formatShipmentDate(shipment.shippedDate) ?? "",
      };
    }
    if (isCanceled) {
      return { key: "shipped", label: "Canceled", state: "terminal", meta: "Nothing shipped" };
    }
    const pickup = shipment.scheduledPickupDate
      ? `Pickup ${formatShipmentDate(shipment.scheduledPickupDate)}`
      : null;
    const planned = shipment.plannedShipDate
      ? `Planned ${formatShipmentDate(shipment.plannedShipDate)}`
      : null;
    return {
      key: "shipped",
      label: "Shipped",
      state: isRequested ? "current" : "upcoming",
      meta: pickup ?? planned ?? "Not shipped yet",
    };
  })();

  // The manifest is a stage of the shipment's life, not a separate feature, so
  // it reads as the step it is. Reconciliation is deliberately NOT a step: it
  // is computed at read and can flip back when a corrected manifest arrives,
  // while every step above it is one-way.
  const manifest: LifecycleStep = (() => {
    const attachedKind = shipment.reconciliation.attachedDocumentKind ?? null;
    const isManifest = attachedKind === ShippingDocumentKind.Manifest;
    if (attachedKind != null) {
      return {
        key: "manifest",
        // A manifest is RECEIVED: the signed copy comes back from the TSDF,
        // which is what `manifestReceivedAt` and `manifestSigned` record. A
        // bill of lading is the carrier's own paper, handed over at pickup with
        // no signed return leg, so it is only ever ATTACHED.
        //
        // The noun comes from the kind, not from a boolean. `attachedDocumentKind`
        // is three-valued and an OTHER-kind document is attachable, so a
        // not-a-manifest branch called it a bill of lading while the actions
        // menu three lines away called it a shipping document.
        label: `${attachedDocumentTitle(attachedKind)} ${isManifest ? "received" : "attached"}`,
        state: "done",
        // Date first, like every step above it. The document number is not
        // here because it is a link now, rendered with the step's actions —
        // meta is plain text, and a number you cannot follow is worse than one
        // sitting a line lower.
        meta: [
          formatShipmentDate(shipment.manifestReceivedAt),
          // Both are manifest facts. A bill of lading has no signed copy, and
          // no manifest row to rebuild -- `shipment.manifest` is null for one
          // by definition, so an ungated "Record rebuilding" showed forever on
          // every bill of lading, next to actions that worked fine.
          isManifest && shipment.manifestSigned ? "Signed" : null,
          isManifest && shipment.manifest == null ? "Record rebuilding" : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
    if (isCanceled) {
      return { key: "manifest", label: "Paperwork", state: "inactive", meta: "" };
    }
    return {
      key: "manifest",
      // Neither kind is chosen yet, so the step cannot name one.
      label: "Paperwork",
      state: "upcoming",
      meta: "Not yet attached",
    };
  })();

  return [packaged, requested, shipped, manifest];
}
